import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Sidebar = ({ isOpen, isMobile, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Fermer la sidebar sur mobile quand on clique sur un lien
  const handleLinkClick = () => {
    if (isMobile && isOpen) {
      onToggle();
    }
  };

  const getNavItems = () => {
    if (!user) return [];

    const baseItems = [
      {
        path: '/admin-dashboard',
        label: 'Souscriptions',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        ),
        description: 'Gestion des souscriptions et services'
      },
      {
        path: '/logements',
        label: 'Logements',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        ),
        description: 'Gestion du parc locatif'
      }
    ];

    const accountingItem = {
      path: '/comptabilite',
      label: 'Comptabilité',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      description: 'Statistiques financières'
    };

    // Filtrer selon le rôle
    switch (user.role) {
      case 'admin-generale':
        return [...baseItems, accountingItem];
      case 'agent-boaz':
        return baseItems;
      case 'bailleur':
        return [
          {
            path: '/logements',
            label: 'Mes Logements',
            icon: baseItems[1].icon,
            description: 'Gestion de mes biens'
          }
        ];
      case 'client':
        return [
          {
            path: '/admin-dashboard',
            label: 'Souscriptions',
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            ),
            description: 'Mes souscriptions'
          }
        ];
      default:
        return [];
    }
  };

  const navItems = getNavItems();

  return (
    <>
      {/* Overlay pour mobile */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-screen bg-white shadow-xl z-50 transition-transform duration-300 ease-in-out
        ${isMobile ? 'w-80' : 'w-64'}
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header avec logo */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center -space-x-2">
              <img
                src="/logo-mini-bh.png"
                alt="Boaz-Housing"
                className="w-32 h-32 object-cover flex-shrink-0 -mx-6"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="text-2xl font-bold text-blue-600" style={{display: 'none'}}>BH</div>
              <div className="min-w-0 pl-2">
                <div className="font-semibold text-gray-900 text-base">Boaz-Housing</div>
                <div className="text-xs text-gray-500">Administration</div>
              </div>
            </div>

            {/* Bouton fermer - toujours visible */}
            <button
              onClick={onToggle}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Fermer le menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation principale */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
              Navigation
            </div>

            {navItems.map(({ path, label, icon, description }) => {
              const isActive = location.pathname === path;

              return (
                <Link
                  key={path}
                  to={path}
                  onClick={handleLinkClick}
                  className={`
                    flex items-center px-4 py-3 rounded-xl transition-all duration-200 group
                    ${isActive
                      ? 'bg-blue-50 border border-blue-100 text-blue-700 shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                >
                  <div className={`
                    mr-3 transition-colors duration-200
                    ${isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}
                  `}>
                    {icon}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{label}</div>
                    <div className={`
                      text-xs truncate transition-colors duration-200
                      ${isActive ? 'text-blue-600/70' : 'text-gray-400 group-hover:text-gray-500'}
                    `}>
                      {description}
                    </div>
                  </div>

                  {isActive && (
                    <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Section utilisateur */}
          {user && (
            <div className="border-t border-gray-200 p-4">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-sm">
                    {user.prenom?.charAt(0)}{user.nom?.charAt(0)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">
                    {user.prenom} {user.nom}
                  </div>
                  <div className="text-sm text-gray-500">
                    {user.role === 'admin-generale' ? 'Administrateur' :
                     user.role === 'agent-boaz' ? 'Agent Boaz' :
                     user.role === 'bailleur' ? 'Bailleur' : 'Client'}
                  </div>
                </div>
              </div>

              <button
                onClick={() => {
                  logout();
                  navigate('/login-otp');
                }}
                className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Déconnexion
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;